'use client'

import Link from 'next/link'
import { Anchor, Box, Divider, List, Modal, Text, UnstyledButton, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import type { LegalTextSegment } from '@/tenants/walmart/content/legal'
import { WALMART_ACTION_MAX_WIDTH, WALMART_READING_MAX_WIDTH } from '@/tenants/walmart/theme/layout'
import actionClasses from '@/tenants/walmart/theme/actions.module.css'
import classes from './coordinatorDialog.module.css'

interface CoordinatorDialogProps {
    opened: boolean
    onClose: () => void
    title: string
    items: readonly (readonly LegalTextSegment[])[]
    dismissLabel: string
}

const DESKTOP_DIALOG_WIDTH = 600

export default function CoordinatorDialog({ opened, onClose, title, items, dismissLabel }: CoordinatorDialogProps) {
    const theme = useMantineTheme()
    const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`)

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={title}
            fullScreen={!isDesktop}
            size={DESKTOP_DIALOG_WIDTH}
            radius={16}
            overlayProps={{ color: '#000', backgroundOpacity: 0.6 }}
            padding={0}
            closeButtonProps={{
                size: isDesktop ? 40 : 24,
                iconSize: isDesktop ? 24 : 18,
                'aria-label': 'Close',
            }}
            data-testid="walmart-coordinator-dialog"
            classNames={{
                inner: classes.inner,
                content: classes.content,
                header: classes.header,
                close: classes.close,
                title: classes.title,
                body: classes.body,
            }}
        >
            <Box
                px="md"
                pb="md"
                maw={{ base: WALMART_READING_MAX_WIDTH, md: '100%' }}
                w="100%"
                mx="auto"
                className={classes.scrollArea}
            >
                <List
                    spacing="md"
                    size="sm"
                    c="var(--gm-foreground-fg-primary)"
                    classNames={{ root: classes.list, item: classes.listItem }}
                >
                    {items.map((segments) => (
                        <List.Item key={segments.map((segment) => segment.text).join('').slice(0, 48)}>
                            <Text fz="var(--gm-text-s-font-size)">
                                {segments.map((segment) =>
                                    segment.href ? (
                                        <Anchor
                                            key={`${segment.href}:${segment.text}`}
                                            component={Link}
                                            href={segment.href}
                                            onClick={onClose}
                                            fz="var(--gm-text-s-font-size)"
                                            fw={segment.emphasis ? 600 : 400}
                                            c="inherit"
                                            underline="always"
                                        >
                                            {segment.text}
                                        </Anchor>
                                    ) : (
                                        <Text
                                            key={segment.text.slice(0, 48)}
                                            component="span"
                                            fz="var(--gm-text-s-font-size)"
                                            fw={segment.emphasis ? 600 : 400}
                                        >
                                            {segment.text}
                                        </Text>
                                    ),
                                )}
                            </Text>
                        </List.Item>
                    ))}
                </List>
            </Box>

            <Box
                px={{ base: 'md', md: 24 }}
                pt={{ base: 'md', md: 0 }}
                pb={{ base: 'md', md: 24 }}
                bg="var(--gm-gray-lighter)"
            >
                <Divider
                    color="var(--gm-foreground-fg-outline-primary)"
                    mb={{ base: 'md', md: 24 }}
                    mx={{ base: 'calc(var(--mantine-spacing-md) * -1)', md: -24 }}
                />
                <UnstyledButton
                    onClick={onClose}
                    maw={{ base: '100%', sm: WALMART_ACTION_MAX_WIDTH, md: '100%' }}
                    mx="auto"
                    className={`${actionClasses.filledAction} ${classes.dismiss}`}
                    data-testid="walmart-coordinator-dialog-dismiss"
                >
                    {dismissLabel}
                </UnstyledButton>
            </Box>
        </Modal>
    )
}
